import { useAuth0 } from "@auth0/auth0-react";
import { Avatar } from "@hortis/ui/avatar";
import { IconButton } from "@hortis/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import {
  Logout1,
  RefreshCw5,
  Settings1,
  Settings4,
  Trash3,
  UserEdit,
  UserPlus1,
} from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { Link, useLocation, useRouteContext } from "@tanstack/react-router";
import {
  useGetMeWithSitesQuery,
  useGetOrganisationFromNameQuery,
} from "generated/graphql";
import { useCallback, useState } from "react";
import { Skeleton } from "src/components/skeleton";
import { useSettingsReturnToStore } from "src/features/settings/return-to-store";
import { createFullName } from "src/utils/full-name";
import NavGroupHeader from "./nav-group-header";

export const OrganizationDropdown = ({
  size = "full",
}: {
  size?: "full" | "avatar";
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { logout } = useAuth0();
  const isDesktop = useMediaQuery("sm");
  const [isOpen, setIsOpen] = useState(false);
  const [{ data, fetching, error }] = useGetOrganisationFromNameQuery({
    variables: { organisationSubdomain: subdomain },
  });
  const [{ data: userData }] = useGetMeWithSitesQuery();

  const user = userData?.me;

  const location = useLocation();
  const { setReturnTo } = useSettingsReturnToStore();

  const handleLogout = useCallback(() => {
    void logout({
      logoutParams: { returnTo: `${window.location.origin}/auth/login` },
    });
  }, [logout]);

  const org = data?.org;

  if (fetching && org == null) {
    return (
      <div className="flex flex-1 items-center gap-2 px-3 py-1">
        <Skeleton
          width="28px"
          height="28px"
          variant="rounded"
          animation="wave"
        />
        {size === "full" && (
          <Skeleton width="84px" height="24px" animation="wave" />
        )}
      </div>
    );
  }

  if (error != null || org == null) {
    return (
      <div className="flex flex-1 items-center gap-2 py-0.5 pl-3 pr-1 text-xs font-medium">
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              ariaLabel="Refresh"
              onClick={() => {
                window.location.reload();
              }}
              variant="tertiaryGray"
              size="xs"
              className="bg-grey-100"
              icon={RefreshCw5}
            />
          </TooltipTrigger>
          <TooltipContent>Refresh</TooltipContent>
        </Tooltip>
        {size === "full" && "Something went wrong"}
      </div>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        className="truncate rounded-sm outline-hidden ring-grey-200/70 focus-visible:ring-3 lg:rounded-lg"
        data-cy="organisation-dropdown"
      >
        {size === "full" ? (
          <NavGroupHeader className="truncate py-1 pl-2">
            <div className="flex items-center gap-2 overflow-hidden text-left text-sm font-medium">
              <Avatar
                className="h-[28px]! w-[28px]! rounded-md"
                variant="rounded"
                textContent={org.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 1)}
                src={org.avatarUrl ?? undefined}
              />
              <span className="truncate">{org.name}</span>
            </div>
          </NavGroupHeader>
        ) : (
          <Avatar
            variant="rounded"
            size="sm"
            textContent={org.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 1)}
            src={org.avatarUrl ?? undefined}
          />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="z-9999 max-w-xs"
        side={isDesktop ? "right" : "bottom"}
        align={size === "full" ? "start" : "end"}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-3 text-sm text-grey-900">
            <Avatar
              className="h-[40px]! w-[40px]! rounded-lg"
              variant="rounded"
              textContent={org.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 1)}
              src={org.avatarUrl ?? undefined}
            />
            <div>
              <div className="truncate font-medium">{org.name}</div>
              <div className="truncate font-normal text-grey-500">
                {org.subdomain}.hortis.com
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link
              to="/settings/org/general"
              onClick={() => {
                setReturnTo(location.pathname);
                setIsOpen(false);
              }}
              className="flex w-full"
            >
              <Settings1 className="h-4 w-4 text-grey-500" />
              <span className="text-sm">Organization settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/settings/org/members"
              onClick={() => {
                setReturnTo(location.pathname);
                setIsOpen(false);
              }}
              className="flex w-full"
            >
              <UserPlus1 className="h-4 w-4 text-grey-500" />
              <span className="text-sm">Invite & manage members</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/taxa/trash"
              onClick={() => {
                setReturnTo(location.pathname);
                setIsOpen(false);
              }}
              className="flex w-full"
            >
              <Trash3 className="h-4 w-4 text-grey-500" />
              <span className="text-sm">Taxa trash</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {user != null && (
            <>
              <DropdownMenuLabel className="flex items-center gap-3 text-sm text-grey-900">
                <Avatar
                  className="h-[40px]! w-[40px]! rounded-lg"
                  variant="circle"
                  textContent={createFullName(user) ?? user.initials}
                  src={user.avatarUrl ?? undefined}
                />
                <div>
                  <div className="truncate font-medium">
                    {createFullName(user) ?? user.initials}
                  </div>
                  <div className="truncate font-normal text-grey-500">
                    {user.email}
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem asChild>
            <Link
              to="/settings/account/profile"
              onClick={() => {
                setReturnTo(location.pathname);
                setIsOpen(false);
              }}
              className="flex w-full"
            >
              <UserEdit className="h-4 w-4 text-grey-500" />
              <span className="text-sm">Profile settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to="/settings/account/preferences"
              onClick={() => {
                setReturnTo(location.pathname);
                setIsOpen(false);
              }}
              className="flex w-full"
            >
              <Settings4 className="h-4 w-4 text-grey-500" />
              <span className="text-sm">Preferences</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <Logout1 className="h-4 w-4 text-grey-500" />
            <span className="text-sm">Log out</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
